<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="">
          {{ $t('Subcontract.PackagesDashboard.TradeItemSelectorModal.title', { packageName: $props.package.name }) }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="flex flex-col w-full h-full">
        <div class="flex space-x-2 items-center sticky top-0 p-2 bg-white z-10">
          <div class="w-full">
            <AppInputIcon
              v-model="searchText"
              type="text"
              name="variation_orders_filter"
              class="form-input h-10"
              placeholder="Search for trade items..."
            >
              <template #icon>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 flex-shrink-0 text-gray-400">
  <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
</svg>
              </template>
            </AppInputIcon>
          </div>

          <button
            type="button"
            class="btn btn--secondary-blue flex-shrink-0"
            @click="searchText = ''"
          >
            {{ $t('Subcontract.PackagesDashboard.TradeItemSelectorModal.button.clearSearch') }}
          </button>
        </div>

        <div class="flex flex-col space-y-3">
          <TradeRow
            v-for="trade in trades"
            :key="trade.id"
            :trade="trade"
            :selected-trade-items="selectedTradeItems"
            :package="$props.package"
            @select-trade-item="$emit('selectTradeItem', $event)"
            @deselect-trade-item="$emit('deselectTradeItem', $event)"
          />

          <p
            v-if="trades.length === 0"
            class="font-semibold text-center"
          >
            {{ $t('Subcontract.PackagesDashboard.TradeItemSelectorModal.message.noTradeItemsMatch') }}
          </p>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-between">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click.prevent="$emit('close')"
        >
          {{ $t('Subcontract.PackagesDashboard.TradeItemSelectorModal.button.close') }}
        </button>

        <button
          type="button"
          class="btn btn--tertiary-blue"
          @click="$emit('newTradeItem')"
        >
          {{ $t('Subcontract.PackagesDashboard.TradeItemSelectorModal.button.createNewTradeItem') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import Package from '@/models/Package';
import TradeItem from '@/models/TradeItem';
import { useProjectsStore } from '@/store/projects';
import { useTradesStore } from '@/store/trades';
import { computed, ref } from 'vue';
import TradeRow from './TradeRow.vue';

const props = defineProps<{
  package: Package;
  selectedTradeItems: TradeItem[];
}>();

const emits = defineEmits<{
  (e: 'close');
  (e: 'newTradeItem');
  (e: 'selectTradeItem', tradeItem: TradeItem);
  (e: 'deselectTradeItem', tradeItem: TradeItem);
}>();

const tradesStore = useTradesStore();
const projectsStore = useProjectsStore();
const searchText = ref('');

const trades = computed(() => {
  const tradesQuery = tradesStore.models
    .where('boq_type', 'direct')
    .where('project_id', projectsStore.project.id)
    .with('trade_items', (query) => {
      query.with('package_trade_items', (query) => {
        query.with('package');
      })
        .where((tradeItem) => tradeItem.budget > 0 || tradeItem.has_children)
        .whereNull('parent_id')
        .orderBy('sort_order');
    })
    .whereHas('trade_items', (query) => {
      query.where('budget', (budget) => budget > 0);
    });

  if(searchText.value) {
    tradesQuery.whereHas('trade_items', (query) => {
      query.where('name', (name) => {
        return name.toLowerCase().includes(searchText.value.toLowerCase());
      });
    });
  }

  return tradesQuery
    .orderBy('sort_order')
    .get();
});
</script>

<style scoped></style>
